import { z } from "zod";
import { moneyInCurrency, parseMoneyValue } from "../money-value";

const numericValueSchema = z.union([z.number(), z.string().trim().min(1)]).pipe(z.coerce.number<string | number>().finite());
const nullableStringSchema = z.string().nullable().optional();
const identifierSchema = z.union([z.string(), z.number()]);

export const userSchema = z.object({
  id: identifierSchema,
  email: z.string(),
  username: nullableStringSchema,
  avatar: z.string().default(""),
  balance: numericValueSchema.default(0),
  currency: z.string().default("USD"),
  active: z.boolean().default(false),
  verificated: z.boolean().default(false),
  can_withdraw: z.boolean().default(false),
  turnover: z.unknown().default(null).transform((value, ctx) => {
    const parsed = parseMoneyValue(value);
    if (!parsed) ctx.addIssue({ code: "custom", message: "Invalid turnover amount" });
    return parsed ?? { amount: 0, currency: null };
  }),
  created: z.string().default(""),
  minimal_deposit: numericValueSchema.default(0),
  minimal_withdraw: numericValueSchema.optional(),
  level: numericValueSchema.optional(),
  lang: z.string().optional(),
  has_2fa: z.boolean().optional(),
  unread_popup_notifications_count: numericValueSchema.optional(),
  unread_notifications_count: numericValueSchema.optional(),
  can_bet: z.boolean().optional(),
  aml_verified: z.boolean().optional(),
  is_banned: z.boolean().optional()
}).passthrough().transform((user) => ({
  ...user,
  // Turnover may arrive as null or as a string with its own currency marker; keep it in the account currency.
  turnover: moneyInCurrency(user.turnover, user.currency)
}));

export const collectionSummarySchema = z.object({
  id: identifierSchema,
  name: z.string(),
  image: z.string(),
  blockchain: z.string().default(""),
  author: z.string().default(""),
  follow: numericValueSchema.default(0),
  created: z.string().optional()
}).passthrough();

export const marketplaceNftSchema = z.object({
  id: identifierSchema,
  image: z.string(),
  number: identifierSchema,
  blockchain: z.string().default(""),
  price: numericValueSchema.default(0),
  currency: z.string().default("USD")
}).passthrough();

export const collectionSchema = z.object({
  name: z.string(),
  author: z.string().default(""),
  in_own: numericValueSchema.default(0),
  min_price: numericValueSchema.default(0),
  max_price: numericValueSchema.default(0),
  nfts: z.array(marketplaceNftSchema).default([])
}).passthrough();

const pricePointSchema = z.object({
  time: z.union([z.string(), z.number()]),
  value: numericValueSchema
}).passthrough();

export const nftDetailsSchema = z.object({
  id: identifierSchema.optional(),
  collection_id: identifierSchema,
  collection_name: z.string(),
  number: identifierSchema,
  image: z.string(),
  blockchain: z.string().default(""),
  price: numericValueSchema.default(0),
  currency: z.string().default("USD"),
  is_sale: z.boolean().default(false),
  sale_price: numericValueSchema.nullish().transform((value) => value ?? 0),
  is_own: z.boolean().default(false),
  own_id: identifierSchema.nullish(),
  prices: z.array(pricePointSchema).default([])
}).passthrough();

export const searchResultSchema = z.object({
  id: identifierSchema,
  image: z.string(),
  blockchain: z.string().default(""),
  collection_name: z.string(),
  number: identifierSchema,
  price: numericValueSchema.default(0),
  currency: z.string().default("USD")
}).passthrough();

export const ownedNftSchema = z.object({
  id: identifierSchema,
  status: z.union([z.boolean(), z.number()]).transform(Boolean),
  sale_price: numericValueSchema.nullish().transform((value) => value ?? 0),
  buy_price: numericValueSchema.optional(),
  buy_date: nullableStringSchema,
  sale_date: nullableStringSchema,
  pic: z.object({
    id: identifierSchema,
    collection_id: identifierSchema.optional(),
    image: z.string(),
    number: identifierSchema,
    price: numericValueSchema.default(0),
    collection: z.object({
      name: z.string(),
      blockchain: z.string().default("")
    }).passthrough()
  }).passthrough()
}).passthrough();

export const notificationSchema = z.object({
  key: identifierSchema.optional(),
  icon: numericValueSchema.pipe(z.number().int()).default(0),
  title: z.string().optional(),
  description: z.string().optional(),
  code: z.string().nullish(),
  type: z.string().nullish(),
  event: z.string().nullish(),
  eventCode: z.string().nullish(),
  created: z.string(),
  is_read: z.boolean().optional(),
  balance_before: numericValueSchema.nullable().optional(),
  balance_after: numericValueSchema.nullable().optional()
}).passthrough().refine((notification) => typeof notification.title === "string" || typeof notification.key === "string", "Missing notification text").transform((notification) => {
  const title = notification.title ?? String(notification.key);
  return { ...notification, title, description: notification.description ?? title };
});

export const paymentMethodSchema = z.object({
  api_id: numericValueSchema.pipe(z.number().int()),
  label: z.string(),
  icon: z.string().default(""),
  min_dep: numericValueSchema.optional(),
  symbol: z.string().optional(),
  network: z.string().optional(),
  regex: z.string().nullish()
}).passthrough();

const paymentDetailValueSchema = z.union([z.string(), z.number(), z.boolean(), z.null()]);

export const paymentSessionSchema = z.object({
  id: identifierSchema,
  min_dep: numericValueSchema.optional(),
  details: z.record(z.string(), paymentDetailValueSchema).optional(),
  action_url: z.string().url().optional(),
  pay_url: z.string().url().optional(),
  url: z.string().url().optional(),
  network: z.string().optional(),
  wallet: z.string().optional(),
  symbol: z.string().optional()
}).passthrough();

export const financeHistorySchema = z.object({
  id: identifierSchema.optional(),
  type: numericValueSchema.pipe(z.number().int()),
  amount: numericValueSchema,
  currency: z.union([z.string(), z.number()]),
  method: numericValueSchema.pipe(z.number().int()),
  status: numericValueSchema.pipe(z.number().int()),
  comment: numericValueSchema.pipe(z.number().int()).default(0),
  created: z.string(),
  details: nullableStringSchema,
  bank: nullableStringSchema
}).passthrough();

export const nftHistorySchema = z.object({
  id: identifierSchema.optional(),
  status: numericValueSchema.pipe(z.number().int()),
  collection_name: z.string(),
  pic_id: identifierSchema,
  sale_price: numericValueSchema.nullish().transform((value) => value ?? 0),
  sale_date: nullableStringSchema,
  buy_date: nullableStringSchema,
  buy_price: numericValueSchema.nullish().transform((value) => value ?? undefined)
}).passthrough();

export const favouriteSchema = z.union([
  identifierSchema,
  z.object({
    id: identifierSchema.optional(),
    nft_id: identifierSchema.optional(),
    pic: z.object({ id: identifierSchema.optional() }).passthrough().optional()
  }).passthrough()
]);

/** Response schemas are not published; validate the fields used by the session UI. */
export const deviceSessionSchema = z.object({
  id: identifierSchema.optional(),
  session_id: identifierSchema.optional(),
  device: z.string().nullish(),
  user_agent: z.string().nullish(),
  ip: z.string().nullish(),
  created: z.string().nullish(),
  last_active: z.string().nullish(),
  current: z.boolean().optional(),
  is_current: z.boolean().optional(),
  created_at: z.string().nullish(),
  is_mobile: z.boolean().optional()
}).passthrough().transform((session) => ({
  ...session,
  ...(session.is_current !== undefined ? { current: session.is_current } : {}),
  ...(session.created_at != null ? { created: session.created_at } : {})
}));

export const deviceSessionsSchema = z.union([
  z.array(deviceSessionSchema),
  z.object({ sessions: z.array(deviceSessionSchema) }).passthrough().transform((value) => value.sessions)
]);

export const twoFactorSetupSchema = z.object({
  secret: z.string().default(""),
  url: z.string().default("")
}).passthrough();

export const activityEventSchema = z.object({
  id: identifierSchema.optional(),
  text: z.string(),
  ts: numericValueSchema.optional(),
  color: z.string().nullish()
}).passthrough();

export const activityFeedSchema = z.union([
  z.array(activityEventSchema),
  z.object({ events: z.array(activityEventSchema) }).passthrough().transform((value) => value.events)
]);

const popupTextSchema = z.string().nullish();

export const popupNotificationSchema = z.object({
  id: identifierSchema.optional(),
  title: popupTextSchema,
  heading: popupTextSchema,
  subject: popupTextSchema,
  name: popupTextSchema,
  description: popupTextSchema,
  text: popupTextSchema,
  body: popupTextSchema,
  message: popupTextSchema,
  content: popupTextSchema,
  type: popupTextSchema,
  code: popupTextSchema,
  event: popupTextSchema,
  eventCode: popupTextSchema,
  key: popupTextSchema
}).passthrough().transform((popup) => ({
  ...popup,
  title: [popup.title, popup.heading, popup.subject, popup.name].find((value) => value?.trim()) ?? "",
  description: popup.description ?? "",
  text: [popup.text, popup.description, popup.body, popup.message, popup.content].find((value) => value?.trim()) ?? ""
}));

const popupListSchema = z.array(popupNotificationSchema);
export const popupNotificationsSchema = z.union([
  popupListSchema,
  z.object({ items: popupListSchema }).passthrough().transform((value) => value.items),
  z.object({ data: popupListSchema }).passthrough().transform((value) => value.data),
  z.object({ results: popupListSchema }).passthrough().transform((value) => value.results),
  z.object({ notifications: popupListSchema }).passthrough().transform((value) => value.notifications),
  z.object({ popups: popupListSchema }).passthrough().transform((value) => value.popups),
  z.object({ list: popupListSchema }).passthrough().transform((value) => value.list)
]);

const optionalNumberSchema = numericValueSchema.optional();
const seriesPointSchema = z.object({ time: z.union([z.string(), z.number()]), value: numericValueSchema }).passthrough();

export const collectionStatsSchema = z.object({
  floor_price: optionalNumberSchema,
  total_volume: optionalNumberSchema,
  owners: optionalNumberSchema,
  listed: optionalNumberSchema,
  listed_percent: optionalNumberSchema,
  supply: optionalNumberSchema,
  description: z.string().nullish(),
  currency: z.string().optional(),
  floor_history: z.array(seriesPointSchema).optional()
}).passthrough();

const traitSchema = z.object({
  trait_type: z.string().optional(),
  name: z.string().optional(),
  value: z.union([z.string(), z.number()]),
  rarity_percent: optionalNumberSchema
}).passthrough();

export const nftExtraSchema = z.object({
  traits: z.array(traitSchema).optional(),
  rarity_rank: optionalNumberSchema,
  rarity_score: optionalNumberSchema,
  supply: optionalNumberSchema,
  contract_address: z.string().nullish(),
  token_standard: z.string().nullish()
}).passthrough();

const auctionDateSchema = z.string().transform((value) => {
  const normalized = value.replace(/(\.\d{3})\d+/, "$1");
  return /^\d{4}-\d{2}-\d{2}T\d{2}:\d{2}:\d{2}(?:\.\d+)?$/.test(normalized)
    ? `${normalized}Z`
    : normalized;
}).refine((value) => Number.isFinite(Date.parse(value)), "Invalid auction time");

/** Platform-run auction lot. Only the platform creates lots; users bid or buy out. */
const auctionPayloadSchema = z.object({
  id: identifierSchema.optional(),
  image_id: identifierSchema.optional(),
  collection_id: identifierSchema.optional(),
  collection_name: z.string().optional(),
  number: identifierSchema.optional(),
  image: z.string().optional(),
  blockchain: z.string().optional(),
  status: z.union([z.enum(["active", "upcoming", "ended"]), z.literal(0), z.literal(1), z.literal(2)]).optional(),
  starts: auctionDateSchema.nullish(),
  ends: auctionDateSchema,
  start_price: optionalNumberSchema,
  current_bid: numericValueSchema.nullish().transform((value) => value ?? undefined),
  min_bid: optionalNumberSchema,
  buy_now_price: optionalNumberSchema,
  bids: optionalNumberSchema,
  currency: z.string().optional(),
  is_leading: z.boolean().optional(),
  my_bid: numericValueSchema.nullish()
}).passthrough().refine((lot) => lot.status !== undefined ||
  Date.parse(lot.ends) <= Date.now() ||
  (typeof lot.starts === "string" && Number.isFinite(Date.parse(lot.starts))), "Unknown auction phase").transform((lot) => {
  if (typeof lot.status !== "number") return { ...lot, status: lot.status };
  // The numeric-status API returns a bid increment and a suggested next amount in my_bid.
  const minimum = lot.bids && lot.current_bid !== undefined
    ? Math.round((lot.current_bid + (lot.min_bid ?? 0)) * 100) / 100
    : lot.start_price;
  return {
    ...lot,
    status: lot.status === 2 ? "ended" as const : lot.status === 1 ? "upcoming" as const : "active" as const,
    min_bid: minimum,
    my_bid: lot.bids && lot.is_leading ? lot.current_bid ?? null : null
  };
});

export const auctionSchema = z.preprocess((value) => {
  if (typeof value !== "object" || value === null || Array.isArray(value)) return value;
  const lot = value as Record<string, unknown>;
  return { ...lot, ends: lot.ends ?? lot.end };
}, auctionPayloadSchema);

const bestWorstSchema = z.object({ name: z.string(), image: z.string().optional(), pnl: numericValueSchema, id: identifierSchema.optional() }).passthrough();

export const portfolioSchema = z.object({
  value: optionalNumberSchema,
  count: optionalNumberSchema,
  unrealized_pnl: optionalNumberSchema,
  realized_pnl: optionalNumberSchema,
  total_spent: optionalNumberSchema,
  total_received: optionalNumberSchema,
  best: bestWorstSchema.nullish(),
  worst: bestWorstSchema.nullish()
}).passthrough();

/** Ranking exports can wrap numbers in { source, parsedValue }; missing metrics stay absent. */
const rankingNumberSchema = z.union([
  numericValueSchema,
  z.object({ parsedValue: numericValueSchema }).transform((value) => value.parsedValue)
]).nullish().transform((value) => value ?? undefined);

/** The live ranking returns NFT records in `items`, rather than an item count. */
const rankingItemsSchema = z.union([
  numericValueSchema.pipe(z.number().int().nonnegative()),
  z.array(z.object({ id: identifierSchema }).passthrough()).transform((items) => items.length)
]).nullish().transform((value) => value ?? undefined);

export const rankingRowSchema = z.object({
  collection_id: identifierSchema,
  name: z.string(),
  image: z.string().default(""),
  blockchain: z.string().default(""),
  floor_price: rankingNumberSchema,
  volume: rankingNumberSchema,
  change_percent: rankingNumberSchema,
  owners: rankingNumberSchema,
  items: rankingItemsSchema,
  currency: z.string().nullish().transform((value) => value ?? undefined)
}).passthrough();

const listOrWrapped = <T extends z.ZodType>(item: T, key: string) => z.union([z.array(item), z.object({ [key]: z.array(item) }).passthrough().transform((value) => value[key] as z.infer<T>[])]);
export const auctionsSchema = listOrWrapped(auctionSchema, "auctions");
export const portfolioHistorySchema = listOrWrapped(seriesPointSchema, "points");
export const rankingSchema = listOrWrapped(rankingRowSchema, "rows");

export const collectionsSchema = z.array(collectionSummarySchema);
export const searchResultsSchema = z.array(searchResultSchema);
export const ownedNftsSchema = z.array(ownedNftSchema);
export const notificationsSchema = z.array(notificationSchema);
export const paymentMethodsSchema = z.array(paymentMethodSchema);
export const financeHistoryListSchema = z.array(financeHistorySchema);
export const nftHistoryListSchema = z.array(nftHistorySchema);
export const favouritesSchema = z.array(favouriteSchema);

export type User = z.infer<typeof userSchema>;
export type CollectionSummary = z.infer<typeof collectionSummarySchema>;
export type MarketplaceNft = z.infer<typeof marketplaceNftSchema>;
export type CollectionDetails = z.infer<typeof collectionSchema>;
export type NftDetails = z.infer<typeof nftDetailsSchema>;
export type SearchResult = z.infer<typeof searchResultSchema>;
export type OwnedNft = z.infer<typeof ownedNftSchema>;
export type Notification = z.infer<typeof notificationSchema>;
export type PaymentMethod = z.infer<typeof paymentMethodSchema>;
export type PaymentSession = z.infer<typeof paymentSessionSchema>;
export type FinanceHistory = z.infer<typeof financeHistorySchema>;
export type NftHistory = z.infer<typeof nftHistorySchema>;
export type CollectionStats = z.infer<typeof collectionStatsSchema>;
export type NftExtra = z.infer<typeof nftExtraSchema>;
export type Auction = z.infer<typeof auctionSchema>;
export type Portfolio = z.infer<typeof portfolioSchema>;
export type RankingRow = z.infer<typeof rankingRowSchema>;
export type SeriesPoint = z.infer<typeof seriesPointSchema>;
export type DeviceSession = z.infer<typeof deviceSessionSchema>;
export type TwoFactorSetup = z.infer<typeof twoFactorSetupSchema>;
export type ActivityEvent = z.infer<typeof activityEventSchema>;
export type PopupNotification = z.infer<typeof popupNotificationSchema>;
export type Favourite = z.infer<typeof favouriteSchema>;
